import React, { useState, useEffect, useMemo } from 'react';
import { Modal, Button } from 'react-bootstrap';
import { useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import { FaCheckCircle, FaTimesCircle } from 'react-icons/fa';
import PaymentModal from '../../modals/payments/PaymentModal/PaymentModal';
import { handleApiResponse, showApiMessage } from '../../modals/global/ApiMessageModal/ApiMessageModal';
import './Sms-Topup.css';
import paymentApi from '../../api/payments/paymentApi';
import smsApi from '../../api/messaging/smsApi';
import { useAuthContext } from '../../context/AuthContext';

const SMS_CREDIT_PRICE = 25;
const WHATSAPP_CREDIT_PRICE = 75;

const SMS_RATES = {
    100: 2500,
    500: 12500,
    900: 22500,
    1000: 25000
};

const WHATSAPP_RATES = {
    100: 7500,
    500: 37500,
    900: 67500,
    1000: 75000
};

const calculatePrice = (count, service, smsRates, whatsappRates) => {
    const numCount = Number(count);
    if (numCount <= 0) return 0;

    if (service === 'sms') {
        if (numCount <= 100) return (numCount / 100) * smsRates[100];
        else if (numCount <= 500) return (numCount / 500) * smsRates[500];
        else if (numCount <= 900) return (numCount / 900) * smsRates[900];
        else return (numCount / 1000) * smsRates[1000];
    }

    if (numCount <= 100) return (numCount / 100) * whatsappRates[100];
    else if (numCount <= 500) return (numCount / 500) * whatsappRates[500];
    else if (numCount <= 900) return (numCount / 900) * whatsappRates[900];
    else return (numCount / 1000) * whatsappRates[1000];
};

const SmsTopup = () => {
    const { events, isAuthenticated, user } = useAuthContext();
    const currentEvent = events?.[0];

    const location = useLocation();
    const [smsCount, setSmsCount] = useState('');
    const [whatsappCount, setWhatsappCount] = useState('');
    const [showPayment, setShowPayment] = useState(false);
    const [creditType, setCreditType] = useState(location.state?.defaultTab || 'sms');
    const [loading, setLoading] = useState(false);
    const [dynamicMnoOptions, setDynamicMnoOptions] = useState([]);
    const [currentPaymentId, setCurrentPaymentId] = useState(null);
    const [initiateLoading, setInitiateLoading] = useState(false);

    const totalAmount = useMemo(() => {
        const smsAmount = creditType === 'sms' ? calculatePrice(smsCount, 'sms', SMS_RATES, WHATSAPP_RATES) : 0;
        const whatsappAmount = creditType === 'whatsapp' ? calculatePrice(whatsappCount, 'whatsapp', SMS_RATES, WHATSAPP_RATES) : 0;
        return smsAmount + whatsappAmount;
    }, [smsCount, whatsappCount, creditType]);

    // Modal states
    const [showSuccessModal, setShowSuccessModal] = useState(false);
    const [showErrorModal, setShowErrorModal] = useState(false);
    const [modalMessage, setModalMessage] = useState('');
    const [transactionDetails, setTransactionDetails] = useState(null);

    // Credit Balances
    const [creditBalances, setCreditBalances] = useState({
        smsBalance: 0,
        smsUsed: 0,
        whatsappBalance: 0,
        whatsappUsed: 0
    });

    useEffect(() => {
        const fetchCredits = async () => {
            try {
                const creditsResponse = await smsApi.getEventCredits();
                let data = creditsResponse?.data;

                if (Array.isArray(data)) {
                    data = data.length > 0 ? data[0] : {};
                } else if (!data) {
                    data = {};
                }

                if (data && typeof data === 'object') {
                    setCreditBalances({
                        smsBalance: data.sms?.balance !== undefined ? data.sms.balance : (data.sms_balance || data.credit_balance || data.credits || 0),
                        smsUsed: data.sms?.used_credits !== undefined ? data.sms.used_credits : (data.sms_used || data.credit_used || data.used || 0),
                        whatsappBalance: data.whatsapp?.balance !== undefined ? data.whatsapp.balance : (data.whatsapp_balance || data.whatsappCredits || 0),
                        whatsappUsed: data.whatsapp?.used_credits !== undefined ? data.whatsapp.used_credits : (data.whatsapp_used || data.whatsappUsed || 0)
                    });
                }
            } catch (err) {
                console.error('Failed to fetch credits:', err);
            }
        };

        if (isAuthenticated) {
            fetchCredits();
        }
    }, [isAuthenticated]);

    useEffect(() => {
        if (location.state?.defaultTab) {
            setCreditType(location.state.defaultTab);
            // Clear opposite count when switching tabs via routing
            if (location.state.defaultTab === 'sms') {
                setWhatsappCount('');
            } else {
                setSmsCount('');
            }
        }
    }, [location.state?.defaultTab]);

    const handleSmsInputChange = (e) => {
        const value = e.target.value;
        setSmsCount(value);
    };

    const handleWhatsappInputChange = (e) => {
        const value = e.target.value;
        setWhatsappCount(value);
    };

    const handleCreditTypeSelect = (type) => {
        setCreditType(type);
        if (type === 'sms') {
            setWhatsappCount('');
        } else {
            setSmsCount('');
        }
    };

    const handleCardClick = (quantity, service) => {
        if (service === 'sms') {
            setSmsCount(quantity);
        } else {
            setWhatsappCount(quantity);
        }
    };

    const handleNextClick = async () => {
        if ((creditType === 'sms' && !smsCount) ||
            (creditType === 'whatsapp' && !whatsappCount)) {
            toast.warning('Tafadhali ingiza kiasi cha credits unachotaka kununua');
            return;
        }

        setInitiateLoading(true);
        try {
            // Determine service based on creditType
            let serviceType = "sms_credits";
            if (creditType === 'whatsapp') serviceType = "whatsapp_credits";
            else if (creditType === 'both') serviceType = "sms_credits";

            const paymentData = {
                service: serviceType,
                amount: totalAmount,
                phone_number: user?.mobile || user?.phone_number || user?.phone || "0000000000",
                customer_name: currentEvent?.client_name || user?.name || 'Admin',
                credits: creditType !== 'whatsapp' ? (parseInt(smsCount) || 0) : (parseInt(whatsappCount) || 0)
            };

            const initiateResponse = await paymentApi.initiatePayment(paymentData);
            const paymentPayload = initiateResponse?.data || initiateResponse;

            if (initiateResponse?.success || initiateResponse?.status === 800 || initiateResponse?.status === 201 || paymentPayload?.payment) {
                const paymentId = paymentPayload?.payment?.id || paymentPayload?.id;
                const options = paymentPayload?.mno_options || [];

                if (!paymentId) {
                    throw new Error('Could not retrieve payment ID from initiation response.');
                }

                setCurrentPaymentId(paymentId);
                setDynamicMnoOptions(options);

                setShowPayment(true);
            } else {
                handleApiResponse(initiateResponse);
            }
        } catch (err) {
            console.error('Payment initiation error:', err);
            handleApiResponse(err);
        }
    };

    return (
        <div className="container-fluid mt-4 mb-5">
            <br></br>

            {/* Unified Credit Balance & Buy Selector */}
            <div className="unified-credit-selector mb-5">
                {/* SMS Unified Card */}
                <div
                    className={`unified-credit-card sms ${creditType === 'sms' ? 'selected' : ''}`}
                    onClick={() => handleCreditTypeSelect('sms')}
                >
                    <div className="unified-card-header">
                        <div className="unified-icon-wrapper sms-icon">
                            <i className="bi bi-envelope-fill"></i>
                        </div>
                        <div className="unified-balance">
                            <span className="balance-label">Credit Balance</span>
                            <span className="balance-value">{creditBalances.smsBalance}</span>
                            <span className="used-label">Used: {creditBalances.smsUsed}</span>
                        </div>
                    </div>
                    <div className="unified-card-action">
                        <div className="action-text-wrapper">
                            <span className="action-title">Buy SMS Credits</span>
                            <small>1 credit = TZS {SMS_CREDIT_PRICE}</small>
                        </div>
                        <div className="radio-indicator">
                            <i className={`bi ${creditType === 'sms' ? 'bi-check-circle-fill' : 'bi-circle'}`}></i>
                        </div>
                    </div>
                </div>

                {/* WhatsApp Unified Card */}
                <div
                    className={`unified-credit-card whatsapp ${creditType === 'whatsapp' ? 'selected' : ''}`}
                    onClick={() => handleCreditTypeSelect('whatsapp')}
                >
                    <div className="unified-card-header">
                        <div className="unified-icon-wrapper wa-icon">
                            <i className="bi bi-whatsapp"></i>
                        </div>
                        <div className="unified-balance">
                            <span className="balance-label">Credit Balance</span>
                            <span className="balance-value">{creditBalances.whatsappBalance}</span>
                            <span className="used-label">Used: {creditBalances.whatsappUsed}</span>
                        </div>
                    </div>
                    <div className="unified-card-action">
                        <div className="action-text-wrapper">
                            <span className="action-title">Buy WhatsApp Credits</span>
                            <small>1 credit = TZS {WHATSAPP_CREDIT_PRICE}</small>
                        </div>
                        <div className="radio-indicator">
                            <i className={`bi ${creditType === 'whatsapp' ? 'bi-check-circle-fill' : 'bi-circle'}`}></i>
                        </div>
                    </div>
                </div>
            </div>

            {creditType === 'sms' && (
                <div className="fade-in">
                    <h4 className="text-center mt-5 mb-4 fw-bold text-secondary">Select SMS Package</h4>
                    <div className="sms-card-grid">
                        {[
                            { label: 'Starter', quantity: 100, price: `TZS ${SMS_RATES[100].toLocaleString()}` },
                            { label: 'Popular', quantity: 500, price: `TZS ${SMS_RATES[500].toLocaleString()}` },
                            { label: 'Pro', quantity: 900, price: `TZS ${SMS_RATES[900].toLocaleString()}` },
                            { label: 'Enterprise', quantity: 1000, price: `TZS ${SMS_RATES[1000].toLocaleString()}` }
                        ].map((item, index) => (
                            <div
                                key={`sms-${index}`}
                                className={`sms-card ${Number(smsCount) === item.quantity ? 'selected' : ''}`}
                                onClick={() => handleCardClick(item.quantity, 'sms')}
                            >
                                <div className="sms-card-header">{item.label}</div>
                                <div className="sms-quantity">{item.quantity} Credits</div>
                                <div className="sms-price">{item.price}</div>
                            </div>
                        ))}
                    </div>
                    <div className="sms-input-wrapper">
                        <input
                            type="number"
                            placeholder="Or enter custom SMS quantity e.g. 150"
                            className="sms-input"
                            value={smsCount}
                            onChange={handleSmsInputChange}
                        />
                    </div>
                </div>
            )}

            {creditType === 'whatsapp' && (
                <div className="fade-in">
                    <h4 className="text-center mt-5 mb-4 fw-bold text-secondary">Select WhatsApp Package</h4>
                    <div className="sms-card-grid">
                        {[
                            { label: 'Starter', quantity: 100, price: `TZS ${WHATSAPP_RATES[100].toLocaleString()}` },
                            { label: 'Popular', quantity: 500, price: `TZS ${WHATSAPP_RATES[500].toLocaleString()}` },
                            { label: 'Pro', quantity: 900, price: `TZS ${WHATSAPP_RATES[900].toLocaleString()}` },
                            { label: 'Enterprise', quantity: 1000, price: `TZS ${WHATSAPP_RATES[1000].toLocaleString()}` }
                        ].map((item, index) => (
                            <div
                                key={`wa-${index}`}
                                className={`sms-card whatsapp-card ${Number(whatsappCount) === item.quantity ? 'selected' : ''}`}
                                onClick={() => handleCardClick(item.quantity, 'whatsapp')}
                            >
                                <div className="sms-card-header text-success">{item.label}</div>
                                <div className="sms-quantity">{item.quantity} Credits</div>
                                <div className="sms-price">{item.price}</div>
                            </div>
                        ))}
                    </div>
                    <div className="sms-input-wrapper">
                        <input
                            type="number"
                            placeholder="Or enter custom WhatsApp quantity e.g. 150"
                            className="sms-input whatsapp-card-input"
                            value={whatsappCount}
                            onChange={handleWhatsappInputChange}
                        />
                    </div>
                </div>
            )}

            <div className="checkout-section">
                <div className="checkout-content">
                    <div className="checkout-info">
                        <span className="checkout-label">Total Amount Due</span>
                        <div className="checkout-price">
                            <span className="currency">TZS</span>
                            <span className="amount">{totalAmount.toLocaleString('en-US', {
                                minimumFractionDigits: 0,
                                maximumFractionDigits: 0
                            })}</span>
                        </div>
                    </div>
                    <button
                        className={`checkout-button ${totalAmount > 0 && !initiateLoading ? 'active' : 'disabled'}`}
                        onClick={handleNextClick}
                        disabled={totalAmount === 0 || initiateLoading}
                    >
                        {initiateLoading ? 'Initiating...' : 'Proceed to Payment'} <i className="bi bi-arrow-right"></i>
                    </button>
                </div>
            </div>

            {/* Global Payment Modal */}
            <PaymentModal
                show={showPayment}
                handleClose={() => setShowPayment(false)}
                amount={totalAmount}
                title="Kamilisha Malipo"
                paymentId={currentPaymentId}
                onPaymentSuccess={(data) => {
                    setTransactionDetails({
                        amount: totalAmount,
                        sms: smsCount,
                        whatsapp: whatsappCount,
                        paymentMethod: data.payment?.payment_method || 'Online',
                        phone: data.payment?.phone_number || '',
                        transactionId: currentPaymentId
                    });
                    setShowPayment(false);
                    setShowSuccessModal(true);
                    
                    setTimeout(() => {
                        setSmsCount('');
                        setWhatsappCount('');
                        setShowPayment(false);
                        setCreditType('sms');
                    }, 3000);
                }}
                onPaymentError={(err) => {
                    setModalMessage(err.message || 'Imeshindwa kuchakata malipo. Tafadhali jaribu tena.');
                    setShowPayment(false);
                    setShowErrorModal(true);
                }}
                mnoOptions={dynamicMnoOptions}
                summaryChildren={
                    <>
                        {creditType !== 'whatsapp' && (
                            <div className="summary-row">
                                <span><i className="bi bi-envelope text-primary me-2"></i> SMS Credits</span>
                                <strong>{smsCount || 0}</strong>
                            </div>
                        )}
                        {creditType !== 'sms' && (
                            <div className="summary-row">
                                <span><i className="bi bi-whatsapp text-success me-2"></i> WhatsApp Credits</span>
                                <strong>{whatsappCount || 0}</strong>
                            </div>
                        )}
                    </>
                }
            />

            {/* Success Modal */}
            <Modal show={showSuccessModal} onHide={() => setShowSuccessModal(false)} centered backdrop="static">
                <Modal.Body className="text-center py-5">
                    <div className="mb-4">
                        <div className="rounded-circle bg-success bg-opacity-10 d-inline-flex p-4">
                            <FaCheckCircle size={64} className="text-success" />
                        </div>
                    </div>
                    <h3 className="fw-bold mb-3">Payment Successful!</h3>
                    <p className="text-muted mb-4">Your purchase was successful. Credits have been added to your account instantly.</p>

                    {transactionDetails && (
                        <div className="bg-light rounded p-4 text-start mb-4">
                            <p className="mb-2 d-flex justify-content-between">
                                <span className="text-muted">Amount Paid:</span>
                                <strong className="fs-5">TZS {transactionDetails.amount.toLocaleString()}</strong>
                            </p>
                            {transactionDetails.sms > 0 && (
                                <p className="mb-2 d-flex justify-content-between">
                                    <span className="text-muted">SMS Credits:</span>
                                    <strong>+{transactionDetails.sms}</strong>
                                </p>
                            )}
                            {transactionDetails.whatsapp > 0 && (
                                <p className="mb-2 d-flex justify-content-between">
                                    <span className="text-muted">WhatsApp Credits:</span>
                                    <strong>+{transactionDetails.whatsapp}</strong>
                                </p>
                            )}
                            <p className="mb-2 d-flex justify-content-between border-top pt-2 mt-2">
                                <span className="text-muted">Method:</span>
                                <strong>{transactionDetails.paymentMethod.toUpperCase()}</strong>
                            </p>
                            <p className="mb-0 d-flex justify-content-between">
                                <span className="text-muted">Phone:</span>
                                <strong>{transactionDetails.phone}</strong>
                            </p>
                        </div>
                    )}
                    <Button variant="success" size="lg" className="w-100 px-5 rounded-pill" onClick={() => setShowSuccessModal(false)}>
                        Done
                    </Button>
                </Modal.Body>
            </Modal>

            {/* Error Modal */}
            <Modal show={showErrorModal} onHide={() => setShowErrorModal(false)} centered>
                <Modal.Body className="text-center py-5">
                    <div className="mb-4">
                        <div className="rounded-circle bg-danger bg-opacity-10 d-inline-flex p-4">
                            <FaTimesCircle size={64} className="text-danger" />
                        </div>
                    </div>
                    <h3 className="fw-bold mb-3">Payment Failed</h3>
                    <p className="text-muted mb-4">{modalMessage}</p>
                    <Button variant="danger" size="lg" className="px-5 rounded-pill" onClick={() => setShowErrorModal(false)}>
                        Try Again
                    </Button>
                </Modal.Body>
            </Modal>
        </div>
    );
};

export default SmsTopup;